/**
 * Keybook API client — server-held master-key model.
 *
 * Endpoints:
 *   GET    /api/keybook/feature-status/:tenantId
 *   POST   /api/keybook/enable
 *   GET    /api/keybook/tenants/:tid/entries          (list)
 *   GET    /api/keybook/entries/:id                   (read — server decrypts)
 *   POST   /api/keybook/tenants/:tid/entries          (create — server encrypts)
 *   PATCH  /api/keybook/entries/:id/value             (update value — re-encrypts)
 *   PATCH  /api/keybook/entries/:id/note              (replace only the note — not a new secret)
 *   PATCH  /api/keybook/entries/:id/metadata          (label/file/type)
 *   DELETE /api/keybook/entries/:id
 *   POST   /api/keybook/entries/:id/access            (share with team)
 *   DELETE /api/keybook/entries/:id/access/:teamId    (revoke team)
 *   POST   /api/keybook/entries/:id/usage             (track use)
 *   POST   /api/keybook/entries/:id/label             (set custom label)
 *   POST   /api/keybook/tenants/:tid/entries/resolve-by-file
 *
 * Auth: X-Tenant-Id + X-Org-Email are added by `@papan/api-client`'s
 * default headers (driven by the TenantProvider getters). This module
 * also exposes the active tenantId via getKeybookContext() for callers
 * that need it (e.g. CredentialPrompt building per-tenant URLs).
 */
import { request } from '@papan/api-client'

export type Permission = 'read' | 'write' | 'manage'
/** A grant row's level — `none` is "No access", a narrowing row (the grant hierarchy). */
export type GrantLevel = 'none' | Permission

/** A grant row as the access dialogs read it. */
export interface GrantRow { recipientType: 'team' | 'email' | 'role'; recipientId: string; permission: GrantLevel }

/** What reaches an item from above: each level's rows only for a viewer who
 *  manages that level (else null — something reaches here from there). */
export interface InheritedAccess {
  root: { rows: GrantRow[] | null }
  collection: { id: string; name: string; rows: GrantRow[] | null } | null
}

/** One principal that could have opened a key's current value (plan
 *  2026-10-16_KEYBOOK_EXPOSURE §4.3). `id` = role name / team id / email /
 *  app folder id / tenant id. */
export interface ExposureItem {
  id: string
  name: string | null
  firstAt: string
  /** Null for a person in the filtered answer (D29 — not Manage's viewer). */
  stillHasAccess: boolean | null
  /** Email rows: this is the organization owner (listed once, as the role). */
  isOrganizationOwner?: boolean
  /** Email rows: when this person first OPENED the value in this period (a read
   *  path served it to them), or null. */
  openedAt?: string | null
  /** Email rows: how the person can open it now — the deciding row's kind, and
   *  the level it sat on (null on rows recorded before levels were kept). */
  via?: {
    type: 'direct' | 'team' | 'role' | 'admin-default' | 'owner' | 'email-owner' | 'opened'
    id: string | null
    name: string | null
    level: 'key' | 'collection' | 'root' | 'default' | 'owner' | null
    collectionName: string | null
  } | null
}

/** Who could have opened a key's current value since it was last set. */
export interface KeybookExposure {
  since: string
  epoch: number
  /** Closed periods kept as history. */
  previousPeriods: number
  /** 'full' for Manage; 'filtered' (D29) — people without their current access. */
  view: 'full' | 'filtered'
  groups: { role: ExposureItem[]; team: ExposureItem[]; email: ExposureItem[]; app: ExposureItem[]; tenant: ExposureItem[] }
  people: { total: number; stillHave: number | null; noLonger: number | null }
}

/** One of a PDApp's variables, as its settings list shows it (from the app's `_appsystem/env.json`). */
export interface AppEnvVar {
  name: string
  kind: 'plain' | 'secret'
  value?: string
  /** Declared in env.json but this copy has no value (`_appsystem/settings.json`, #1544). */
  unset?: boolean
  secret?: {
    description: string | null
    /** The key env.json names (a reference) and the field. */
    entryId: string | null
    field: string | null
    /** 'ok' = the app holds the key and you can read it; 'no-access' = the app holds it, you can't read it (it keeps you
     *  from editing the app); 'needs-key' = the app doesn't hold it (a re-upload, never connected, taken away) or none
     *  is chosen. */
    status: 'ok' | 'no-access' | 'needs-key'
    needs?: 'no-key' | 'no-field' | 'unavailable' | 'not-connected'
    label: string | null
    credentialType: string | null
    /** You may connect it as it is, in one click. */
    canConnect: boolean
  }
}

let g_tenantId: string | null = null
let g_orgEmail: string | null = null

/** Mirror of the active tenant + org-email so feature code can read
 *  them without going back through React context. Header injection is
 *  done by `@papan/api-client`, not here. */
export function setKeybookContext(tenantId: string | null, orgEmail: string | null) {
  g_tenantId = tenantId
  g_orgEmail = orgEmail
}

export function getKeybookContext(): { tenantId: string | null; orgEmail: string | null } {
  return { tenantId: g_tenantId, orgEmail: g_orgEmail }
}

// ─── Feature gate ────────────────────────────────────────────────────

export interface FeatureStatus {
  enabled: boolean
  enabledAt?: string
  tenantType: 'personal' | 'organization'
  keyVersion: number
}

export const keybookApi = {
  async featureStatus(tenantId: string): Promise<FeatureStatus> {
    return request(`/api/keybook/feature-status/${tenantId}`)
  },

  async enableKeybook(tenantId: string): Promise<{ ok: boolean; tenantType: 'personal' | 'organization' }> {
    return request('/api/keybook/enable', {
      method: 'POST',
      body: JSON.stringify({ tenantId }),
    })
  },

  // ─── Entries ──────────────────────────────────────────────────────

  async listEntries(tenantId: string): Promise<{
    entries: Array<{
      id: string
      tenantId: string
      ownerType: 'email' | 'team' | 'role'
      /** lowercased email for ownerType='email'; teamId for 'team'; role name for 'role' (display-only). */
      ownerId: string
      sourceLabel: string
      credentialType: string
      /** All file ids this entry is bound to (junction table).
       *  Back-compat scalar list — for richer per-file health info,
       *  read `files` instead. */
      fileIds: string[]
      /** Rich per-file binding info: timestamp of last use, last
       *  success, and last failure reason. Drives health badges in
       *  the keybook detail panel. */
      files: Array<{
        fileId: string
        lastUsedAt: string | null
        lastSuccessAt: string | null
        lastFailureReason: string | null
      }>
      version: number
      oauthRevoked: boolean
      provider: string | null
      /** The collection this key is filed into (§5d), or null = ungrouped. */
      collectionId?: string | null
      updatedBy: string | null
      updatedAt: string
      createdAt: string
      customLabel?: string | null
      lastUsedFileId?: string | null
      lastUsedAt?: string | null
      /** False when the owner is gone — an email no active member holds, or
       *  an owning team that no longer exists. Drives the orphan badge / Claim. */
      ownerActive: boolean
      /** The caller's resolved access on this key (the grant hierarchy). */
      permission?: 'read' | 'write' | 'manage'
      /** Read only through the caller's admin role — shown under "Show as admin". */
      hiddenWithoutAdminView?: boolean
    }>
    /** Whether the caller manages the keybook root (root dialog, collection create). */
    managesRoot?: boolean
    /** The caller's manage_keybook_permission (resolved once): with each key's
     *  `permission`, `keybookAccessView` says whether they may see its access list
     *  and exposure. */
    accessCapability?: 'none' | 'read' | 'write'
  }> {
    return request(`/api/keybook/tenants/${tenantId}/entries`)
  },

  async getEntry(entryId: string): Promise<{
    entry: {
      id: string
      tenantId: string
      ownerType: 'email' | 'team' | 'role'
      ownerId: string
      sourceLabel: string
      credentialType: string
      fileIds: string[]
      data: any
      version: number
      keyVersion: number
      oauthRevoked: boolean
      provider: string | null
      createdBy: string | null
      updatedBy: string | null
      createdAt: string
      updatedAt: string
    }
    myPermission: Permission
    accessRows: Array<{
      recipientType: 'team' | 'email' | 'role'
      /** teamId, lowercase email, or a role name. */
      recipientId: string
      permission: GrantLevel
      grantedBy: string | null
      grantedAt: string
    }>
    /** For a manager: what reaches the key from the root and its collection. */
    inherited?: InheritedAccess | null
    canTransfer?: boolean
  }> {
    return request(`/api/keybook/entries/${entryId}`)
  },

  async createEntry(tenantId: string, payload: {
    ownerType: 'email' | 'team'
    /** Required when ownerType='team' (the team id). For
     *  ownerType='email' the server uses the caller's resolved
     *  orgEmail; any value passed here is ignored. */
    ownerId?: string
    sourceLabel: string
    credentialType?: string
    /** Single optional file binding at create time (legacy shape). */
    fileId?: string
    /** Multiple file bindings at create time. */
    fileIds?: string[]
    provider?: string
    data: unknown
    access?: Array<{
      recipientType: 'team' | 'email' | 'role'
      recipientId: string
      permission: Permission
    }>
  }): Promise<{ ok: boolean; entryId: string }> {
    return request(`/api/keybook/tenants/${tenantId}/entries`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  /** `expectedVersion`: the `version` the value was loaded at — the server refuses
   *  the write (409 KEYBOOK_ENTRY_CHANGED) when the key has changed since. */
  async updateEntryValue(entryId: string, data: unknown, expectedVersion?: number): Promise<{ ok: boolean; version: number }> {
    return request(`/api/keybook/entries/${entryId}/value`, {
      method: 'PATCH',
      body: JSON.stringify({ data, ...(expectedVersion !== undefined && { expectedVersion }) }),
    })
  },

  /** Replaces ONLY the key's note (blank removes it): the secret is untouched, so it
   *  starts no new exposure period. Same version check as a value write. */
  async updateEntryNote(entryId: string, note: string, expectedVersion?: number): Promise<{ ok: boolean; version: number }> {
    return request(`/api/keybook/entries/${entryId}/note`, {
      method: 'PATCH',
      body: JSON.stringify({ note, ...(expectedVersion !== undefined && { expectedVersion }) }),
    })
  },

  async updateEntryMetadata(entryId: string, payload: {
    sourceLabel?: string
    credentialType?: string
  }): Promise<{ ok: boolean }> {
    return request(`/api/keybook/entries/${entryId}/metadata`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    })
  },

  /** Bind an existing file to an entry (idempotent). */
  async bindFile(entryId: string, fileId: string): Promise<{ ok: boolean }> {
    return request(`/api/keybook/entries/${entryId}/files`, {
      method: 'POST',
      body: JSON.stringify({ fileId }),
    })
  },

  async unbindFile(entryId: string, fileId: string): Promise<{ ok: boolean }> {
    return request(`/api/keybook/entries/${entryId}/files/${encodeURIComponent(fileId)}`, {
      method: 'DELETE',
    })
  },

  async deleteEntry(entryId: string): Promise<{ ok: boolean }> {
    return request(`/api/keybook/entries/${entryId}`, {
      method: 'DELETE',
    })
  },

  async addEntryAccess(entryId: string, payload: {
    recipientType: 'team' | 'email' | 'role'
    /** teamId, lowercase email, or a role name. */
    recipientId: string
    permission: GrantLevel
  }): Promise<{ ok: boolean }> {
    return request(`/api/keybook/entries/${entryId}/access`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  async revokeEntryAccess(
    entryId: string,
    recipientType: 'team' | 'email' | 'role',
    recipientId: string,
  ): Promise<{ ok: boolean }> {
    return request(
      `/api/keybook/entries/${entryId}/access/${recipientType}/${encodeURIComponent(recipientId)}`,
      { method: 'DELETE' },
    )
  },

  // ─── Collections (1-level vaults; a collection is a grant scope, §5d) ───
  async listCollections(tenantId: string): Promise<{ collections: Array<{ id: string; name: string; permission: Permission; createdBy: string | null; hiddenWithoutAdminView?: boolean }>; managesRoot?: boolean; accessCapability?: 'none' | 'read' | 'write' }> {
    return request(`/api/keybook/tenants/${tenantId}/collections`)
  },
  async createCollection(tenantId: string, name: string): Promise<{ ok: boolean; id: string; name: string }> {
    return request(`/api/keybook/tenants/${tenantId}/collections`, { method: 'POST', body: JSON.stringify({ name }) })
  },
  async renameCollection(id: string, name: string): Promise<{ ok: boolean }> {
    return request(`/api/keybook/collections/${id}`, { method: 'PATCH', body: JSON.stringify({ name }) })
  },
  async deleteCollection(id: string): Promise<{ ok: boolean }> {
    return request(`/api/keybook/collections/${id}`, { method: 'DELETE' })
  },
  /** Manage → the full list; Write, or Read with manage_keybook_permission ≥ read →
   *  the read-only list (`readOnly: true`, "No access" rows hidden). */
  async getCollectionAccess(id: string): Promise<{ access: GrantRow[]; inherited?: InheritedAccess; readOnly?: boolean }> {
    return request(`/api/keybook/collections/${id}/access`)
  },
  /** A key's access list without its value — full for Manage, read-only (no "No
   *  access" rows, no granter) for Write or the capability. */
  async getEntryAccess(id: string): Promise<{
    readOnly: boolean
    owner: { type: 'email' | 'team' | 'role'; id: string }
    accessRows: Array<GrantRow & { grantedBy?: string | null; grantedAt?: string }>
    inherited: InheritedAccess | null
  }> {
    return request(`/api/keybook/entries/${id}/access`)
  },
  async addCollectionAccess(id: string, payload: GrantRow): Promise<{ ok: boolean }> {
    return request(`/api/keybook/collections/${id}/access`, { method: 'POST', body: JSON.stringify(payload) })
  },
  async revokeCollectionAccess(id: string, recipientType: 'team' | 'email' | 'role', recipientId: string): Promise<{ ok: boolean }> {
    return request(`/api/keybook/collections/${id}/access/${recipientType}/${encodeURIComponent(recipientId)}`, { method: 'DELETE' })
  },
  // ─── The keybook ROOT (grants above every collection and key) ───
  async getRootAccess(tenantId: string): Promise<{
    access: GrantRow[]
    adminDefault: GrantLevel
    canLowerAdmins: boolean
    /** Owner only: items where admins still end up at Manage through a collection or key row. */
    adminManageBelow: Array<{ level: 'collection' | 'key'; id: string; name: string; admins: string[] }>
  }> {
    return request(`/api/keybook/tenants/${tenantId}/keybook-access`)
  },
  async addRootAccess(tenantId: string, payload: GrantRow): Promise<{ ok: boolean }> {
    return request(`/api/keybook/tenants/${tenantId}/keybook-access`, { method: 'POST', body: JSON.stringify(payload) })
  },
  async revokeRootAccess(tenantId: string, recipientType: 'team' | 'email' | 'role', recipientId: string): Promise<{ ok: boolean }> {
    return request(`/api/keybook/tenants/${tenantId}/keybook-access/${recipientType}/${encodeURIComponent(recipientId)}`, { method: 'DELETE' })
  },
  /** File a key into a collection (or null to ungroup). */
  async setEntryCollection(entryId: string, collectionId: string | null): Promise<{ ok: boolean }> {
    return request(`/api/keybook/entries/${entryId}/collection`, { method: 'PATCH', body: JSON.stringify({ collectionId }) })
  },

  /** Transfer ownership of a key to another member or to a team (Rule 4:
   *  needs Manage on the key). `keepManage` (default true) keeps the giver's
   *  Manage when they'd otherwise lose it. The new owner's private nickname, if
   *  any, becomes the key's name. */
  async transferOwner(entryId: string, payload: {
    ownerType: 'email' | 'team'
    /** email · teamId. */
    ownerId: string
    keepManage?: boolean
  }): Promise<{ ok: boolean; sourceLabel: string }> {
    return request(`/api/keybook/entries/${entryId}/ownership`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  async recordEntryUsage(entryId: string, fileId?: string | null): Promise<{ ok: boolean }> {
    return request(`/api/keybook/entries/${entryId}/usage`, {
      method: 'POST',
      body: JSON.stringify({ fileId: fileId ?? null }),
    })
  },

  /** Re-list the GitHub App installation's accessible repos and
   *  rewrite the entry's encrypted blob with the fresh count + names.
   *  github credentialType only — other types reject with 400. */
  async refreshGithubMetadata(entryId: string): Promise<{
    ok: boolean
    repositoryCount: number
    repositoryTruncated: boolean
  }> {
    return request(`/api/keybook/entries/${entryId}/refresh-metadata`, {
      method: 'POST',
    })
  },

  // ─── A PDApp's Variables and secrets (0326) ───────────────────────

  /** The app's variables. `canEdit` = the viewer may change them (else a manager of the app folder: list, and
   *  remove secrets only). A secret's key is named only when the viewer can read it. */
  async getAppEnv(appFolderId: string): Promise<{
    canEdit: boolean; vars: AppEnvVar[]; keybookEnabled: boolean; maxVars: number
    /** What env.json holds that was left out, and why. */
    problems?: string[]
    /** Keys the app holds that no variable names (env.json edited by hand) — listed so they can be taken away. */
    unusedKeys?: Array<{ entryId: string; label: string | null }>
  }> {
    return request(`/api/pdapp/${encodeURIComponent(appFolderId)}/env?fresh=${Date.now()}`)
  },

  /** `renameFrom`: save under `name` and remove that variable in the same write (a rename). */
  async setAppEnv(appFolderId: string, name: string, body: ({ kind: 'plain'; value: string } | { kind: 'secret'; entryId: string; field: string; description?: string }) & { renameFrom?: string }): Promise<{ ok: boolean }> {
    return request(`/api/pdapp/${encodeURIComponent(appFolderId)}/env/${encodeURIComponent(name)}`, { method: 'PUT', body: JSON.stringify(body) })
  },

  /** An editor removes the variable; a manager who may only lower / remove takes its key away (`keyRemoved`). */
  async deleteAppEnv(appFolderId: string, name: string): Promise<{ ok: boolean; keyRemoved?: boolean }> {
    return request(`/api/pdapp/${encodeURIComponent(appFolderId)}/env/${encodeURIComponent(name)}`, { method: 'DELETE' })
  },

  /** Take a key away from the app (one no variable names) — env.json untouched. */
  async deleteAppEnvKey(appFolderId: string, entryId: string): Promise<{ ok: boolean }> {
    return request(`/api/pdapp/${encodeURIComponent(appFolderId)}/env-keys/${encodeURIComponent(entryId)}`, { method: 'DELETE' })
  },

  /** A key's field NAMES (never values) — Read-only on it. */
  async getEntryFieldNames(entryId: string): Promise<{ fields: string[] }> {
    return request(`/api/keybook/entries/${entryId}/field-names`)
  },

  /** The PDApps that hold a key; `canRevoke` = the caller manages it. */
  async getEntryApps(entryId: string): Promise<{ apps: Array<{ appFolderId: string; name: string | null; variables: string[] }>; canRevoke: boolean }> {
    return request(`/api/keybook/entries/${entryId}/apps?fresh=${Date.now()}`)
  },

  async revokeEntryApp(entryId: string, appFolderId: string): Promise<{ ok: boolean }> {
    return request(`/api/keybook/entries/${entryId}/apps/${encodeURIComponent(appFolderId)}`, { method: 'DELETE' })
  },

  /** Who could have opened the key's current value — Manage on the key. */
  async getEntryExposure(entryId: string, opts: { fresh?: boolean } = {}): Promise<KeybookExposure> {
    // `fresh`: a read that must not JOIN an identical one already in flight
    // (request() shares concurrent GETs) — the delete confirm needs the record
    // as of now. The query is ignored by the server; it only makes the path unique.
    return request(`/api/keybook/entries/${entryId}/exposure${opts.fresh ? `?fresh=${Date.now()}-${Math.random().toString(36).slice(2)}` : ''}`)
  },

  /** The caller's own keys in a tenant, each with its exposure — what the leave
   *  confirm lists (they stay in the org, still working). */
  async getOwnedKeysForLeaving(tenantId: string): Promise<{
    keys: Array<{ id: string; label: string; credentialType: string; provider: string | null; exposure: KeybookExposure | null }>
    more: number
  }> {
    return request(`/api/keybook/tenants/${tenantId}/entries/owned-by-me`)
  },


  /** Audit trail for an entry — requires `manage` on the entry (the
   *  server 403s otherwise). Last 200 events, newest first. */
  async getEntryAudit(entryId: string): Promise<{
    events: Array<{
      id: string
      entryId: string | null
      tenantId: string
      eventType: string
      actorUserId: string | null
      actorMemberId: string | null
      targetTeamId: string | null
      targetPermission: string | null
      oldValue: any
      newValue: any
      eventAt: string
    }>
  }> {
    return request(`/api/keybook/entries/${entryId}/audit`)
  },

  async setCustomLabel(entryId: string, customLabel: string | null): Promise<{ ok: boolean }> {
    return request(`/api/keybook/entries/${entryId}/label`, {
      method: 'POST',
      body: JSON.stringify({ customLabel }),
    })
  },

  async resolveByFile(tenantId: string, payload: {
    fileId: string
    label?: string
  }): Promise<{
    found: boolean
    entry?: {
      id: string
      sourceLabel: string
      credentialType: string
      version: number
    }
  }> {
    return request(`/api/keybook/tenants/${tenantId}/entries/resolve-by-file`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  /** Find an OAuth-style entry by provider for the caller. Used by
   *  cred_to_env to detect "do I already have a GitHub token saved?".
   *  Pass `contextRepo` (e.g. "owner/name") so the server scores
   *  candidates by whether they actually cover that repo — without
   *  it, two github creds for two different orgs are indistinguishable
   *  and the form pre-selects whichever happens to come first. */
  async resolveByProvider(tenantId: string, payload: {
    provider: string
    contextRepo?: string
  }): Promise<{
    found: boolean
    entry?: {
      id: string
      sourceLabel: string
      credentialType: string
      version: number
      provider: string | null
    }
  }> {
    return request(`/api/keybook/tenants/${tenantId}/entries/resolve-by-provider`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  /** Validate a Personal Access Token by hitting the provider's API
   *  server-side. Returns owner + accessible repo list so the create
   *  form can show "✅ verified, scope: foo, bar" right after paste.
   *  PAT is sent over HTTPS and not persisted by this route. */
  async validatePat(payload: {
    provider: string
    token: string
  }): Promise<{
    ok: boolean
    error?: string
    accountLogin?: string
    accountType?: 'User' | 'Organization'
    repositoryNames?: string[]
    repositoryCount?: number
    truncated?: boolean
    repositorySelection?: 'all' | 'selected'
    scope?: string
  }> {
    return request(`/api/keybook/pat/validate`, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },
}
